import { createItemIconProvider, dimColor } from '../pixel-icons.js';
import { TREE_LOG_ITEM_IDS, TREE_PLANK_ITEM_IDS } from '../game-data.js';
import { makeStack, clearStack, isStackEmpty, getStackMax } from '../ui/stack-utils.js';
import { createContainerTooltip } from '../ui/container-shared.js';
import { clamp } from '../utils/math-helpers.js';

function createFurnaceState() {
  return {
    input: makeStack(),
    fuel: makeStack(),
    output: makeStack(),
    burnTime: 0,
    burnTotal: 0,
    progress: 0
  };
}

export function createFurnaceSystem({
  world,
  ui,
  itemDefs,
  t,
  onRequestClose = null,
  onTakeOutput = null
}) {
  const FURNACE_BLOCK_ID = 13;
  const TREE_LOG_ITEM_ID_SET = new Set(TREE_LOG_ITEM_IDS);
  const TREE_PLANK_ITEM_ID_SET = new Set(TREE_PLANK_ITEM_IDS);
  const WOOD_FUEL_DURATION = 15.0;
  const CHARCOAL_SMELT_RECIPE = Object.freeze({ outId: 'charcoal', time: 10.0 });

  const SMELT_RECIPES = {
    '8': { outId: 'iron_ingot', time: 10.0 },
    '47': { outId: 'gold_ingot', time: 10.0 },
    '26': { outId: '23', time: 10.0 },
    '28': { outId: '5', time: 10.0 },
    '5': { outId: '30', time: 10.0 },
    '29': { outId: '34', time: 10.0 },
    '40': { outId: '42', time: 10.0 },
    beef_raw: { outId: 'beef_cooked', time: 10.0 },
    mutton_raw: { outId: 'mutton_cooked', time: 10.0 },
    pork_raw: { outId: 'pork_cooked', time: 10.0 },
    chicken_raw: { outId: 'chicken_cooked', time: 10.0 }
  };

  const FUEL_TIME = {
    coal: 80.0,
    '35': 720.0,
    charcoal: 80.0,
    '16': 10.0,
    '18': 15.0,
    '20': 15.0,
    stick: 5.0
  };

  const furnaceStates = new Map();
  let openFurnaceKey = null;

  const iconProvider = createItemIconProvider(itemDefs);
  const overlayEl = document.getElementById('furnace-overlay');
  const titleEl = document.getElementById('furnace-title');
  const hintEl = document.getElementById('furnace-hint');
  const inputSlotEl = document.getElementById('furnace-input-slot');
  const fuelSlotEl = document.getElementById('furnace-fuel-slot');
  const outputSlotEl = document.getElementById('furnace-output-slot');
  const burnFillEl = document.getElementById('furnace-burn-fill');
  const smeltFillEl = document.getElementById('furnace-smelt-fill');
  const closeBtnEl = document.getElementById('furnace-close-btn');
  const tooltipEl = document.getElementById('inventory-tooltip');

  const { getItemDisplayName, hideTooltip, bindSlotTooltip } = createContainerTooltip({ itemDefs, t, tooltipEl });

  function stackLabel(stack) {
    if (!stack || !stack.id || stack.count <= 0) return '';
    const name = getItemDisplayName(stack.id);
    return `${name}${stack.count > 1 ? ` x${stack.count}` : ''}`;
  }

  function furnaceKeyFromPos(x, y, z) {
    return `${Math.round(x)},${Math.round(y)},${Math.round(z)}`;
  }

  function parseFurnaceKey(key) {
    const [x, y, z] = String(key).split(',').map(Number);
    return { x, y, z };
  }

  function cloneStackData(stack) {
    if (!stack || !stack.id || stack.count <= 0) return null;
    return {
      id: String(stack.id),
      count: Math.max(1, Math.floor(stack.count))
    };
  }

  function normalizeStackData(raw) {
    const itemId = String(raw?.id || '');
    if (!itemDefs[itemId]) return null;
    const count = Math.floor(Number(raw?.count));
    if (!Number.isFinite(count) || count <= 0) return null;
    return {
      id: itemId,
      count: Math.min(getStackMax(itemDefs, itemId), count)
    };
  }

  function getOrCreateState(key) {
    if (!furnaceStates.has(key)) furnaceStates.set(key, createFurnaceState());
    return furnaceStates.get(key);
  }

  function takeToInventory(itemId, count) {
    let moved = 0;
    const total = Math.max(0, Math.floor(count));
    for (let i = 0; i < total; i++) {
      if (!ui.addItem(itemId, 1)) break;
      moved += 1;
    }
    return moved;
  }

  function getRecipeForInput(inputId) {
    if (!inputId) return null;
    if (TREE_LOG_ITEM_ID_SET.has(String(inputId))) return CHARCOAL_SMELT_RECIPE;
    return SMELT_RECIPES[inputId] || null;
  }

  function getFuelDuration(itemId) {
    if (TREE_LOG_ITEM_ID_SET.has(String(itemId)) || TREE_PLANK_ITEM_ID_SET.has(String(itemId))) {
      return WOOD_FUEL_DURATION;
    }
    return Number(FUEL_TIME[itemId] || 0);
  }

  function canSmeltNow(state) {
    if (isStackEmpty(state.input)) return false;
    const recipe = getRecipeForInput(state.input.id);
    if (!recipe) return false;

    if (isStackEmpty(state.output)) return true;
    if (state.output.id !== recipe.outId) return false;
    return state.output.count < getStackMax(itemDefs, recipe.outId);
  }

  function consumeFuelIfNeeded(state) {
    if (state.burnTime > 0) return;
    if (isStackEmpty(state.fuel)) return;
    if (!canSmeltNow(state)) return;

    const duration = getFuelDuration(state.fuel.id);
    if (duration <= 0) return;

    state.fuel.count -= 1;
    if (state.fuel.count <= 0) clearStack(state.fuel);
    state.burnTime = duration;
    state.burnTotal = duration;
  }

  function performSmelt(state, recipe) {
    if (!recipe) return false;
    if (!canSmeltNow(state)) return false;

    state.input.count -= 1;
    if (state.input.count <= 0) clearStack(state.input);

    if (isStackEmpty(state.output)) {
      state.output.id = recipe.outId;
      state.output.count = 1;
    } else {
      state.output.count += 1;
    }
    return true;
  }

  function updateState(state, delta) {
    if (!state) return;

    if (state.burnTime > 0) {
      state.burnTime = Math.max(0, state.burnTime - delta);
    }

    consumeFuelIfNeeded(state);
    const recipe = getRecipeForInput(state.input.id);
    const smelting = !!recipe && state.burnTime > 0 && canSmeltNow(state);

    if (!smelting) {
      state.progress = 0;
      return;
    }

    state.progress += delta;
    while (state.progress >= recipe.time) {
      state.progress -= recipe.time;
      if (!performSmelt(state, recipe)) {
        state.progress = 0;
        break;
      }
      if (!canSmeltNow(state)) {
        state.progress = 0;
        break;
      }
    }
  }

  function renderSlot(el, stack, emptyLabelKey) {
    if (!el) return;
    if (!stack || !stack.id || stack.count <= 0) {
      el.className = 'slot furnace-slot';
      el.style.backgroundColor = '#3d434f';
      el.innerHTML = `<span class="slot-key">${t(emptyLabelKey)}</span><span class="slot-icon"></span><span class="slot-count"></span>`;
      return;
    }

    const def = itemDefs[stack.id];
    const iconUrl = iconProvider.getIcon(stack.id);
    el.className = 'slot furnace-slot';
    el.style.backgroundColor = dimColor(def?.color, 0.35) || '#2a2e36';
    el.innerHTML = `
      <span class="slot-key"></span>
      <span class="slot-icon">${iconUrl ? `<img src="${iconUrl}" alt="" draggable="false">` : ''}</span>
      <span class="slot-count">${stack.count > 1 ? stack.count : ''}</span>
    `;
  }

  function currentState() {
    if (!openFurnaceKey) return null;
    return getOrCreateState(openFurnaceKey);
  }

  function renderOpenFurnace() {
    if (!overlayEl || !isOpen()) return;
    const state = currentState();
    if (!state) return;

    if (titleEl) titleEl.innerText = t('furnace.title');
    if (hintEl) hintEl.innerText = t('furnace.hint');
    if (closeBtnEl) closeBtnEl.innerText = t('furnace.close');

    renderSlot(inputSlotEl, state.input, 'furnace.slot.input');
    renderSlot(fuelSlotEl, state.fuel, 'furnace.slot.fuel');
    renderSlot(outputSlotEl, state.output, 'furnace.slot.output');

    if (burnFillEl) {
      const burnPct = state.burnTotal > 0 ? clamp(state.burnTime / state.burnTotal, 0, 1) : 0;
      burnFillEl.style.height = `${Math.round(burnPct * 100)}%`;
    }

    if (smeltFillEl) {
      const recipe = getRecipeForInput(state.input.id);
      const pct = recipe ? clamp(state.progress / recipe.time, 0, 1) : 0;
      smeltFillEl.style.width = `${Math.round(pct * 100)}%`;
    }

    if (inputSlotEl) inputSlotEl.title = '';
    if (fuelSlotEl) fuelSlotEl.title = '';
    if (outputSlotEl) outputSlotEl.title = '';
  }

  function isSmeltableItem(itemId) {
    return !!getRecipeForInput(itemId);
  }

  function isFuelItem(itemId) {
    return getFuelDuration(itemId) > 0;
  }

  function depositFromActive(slot, desiredCount, validateItem = null) {
    const activeId = ui.getActiveItemId();
    const activeCount = ui.getActiveItemCount();
    if (!activeId || activeCount <= 0 || desiredCount <= 0) return 0;
    if (typeof validateItem === 'function' && !validateItem(activeId)) return 0;

    const maxStack = getStackMax(itemDefs, activeId);
    if (isStackEmpty(slot)) {
      const move = Math.min(desiredCount, activeCount, maxStack);
      if (move <= 0) return 0;
      if (!ui.consumeActiveItem(move)) return 0;
      slot.id = activeId;
      slot.count = move;
      return move;
    }

    if (slot.id !== activeId) return 0;
    const space = maxStack - slot.count;
    if (space <= 0) return 0;
    const move = Math.min(desiredCount, activeCount, space);
    if (move <= 0) return 0;
    if (!ui.consumeActiveItem(move)) return 0;
    slot.count += move;
    return move;
  }

  function withdrawToInventory(slot, desiredCount) {
    if (isStackEmpty(slot) || desiredCount <= 0) return 0;
    const move = Math.min(slot.count, desiredCount);
    const moved = takeToInventory(slot.id, move);
    if (moved <= 0) return 0;
    slot.count -= moved;
    if (slot.count <= 0) clearStack(slot);
    return moved;
  }

  function takeOutputToInventory(desiredCount) {
    const state = currentState();
    if (!state || isStackEmpty(state.output) || desiredCount <= 0) return 0;

    const outputId = state.output.id;
    const moved = withdrawToInventory(state.output, desiredCount);
    if (moved <= 0) return 0;
    if (typeof onTakeOutput === 'function') {
      onTakeOutput({
        itemId: outputId,
        count: moved
      });
    }
    return moved;
  }

  function quickMoveIntoSlot(slot, itemId, desiredCount, validateItem = null) {
    if (!itemId || desiredCount <= 0) return 0;
    if (typeof validateItem === 'function' && !validateItem(itemId)) return 0;

    const maxStack = getStackMax(itemDefs, itemId);
    if (isStackEmpty(slot)) {
      const put = Math.min(desiredCount, maxStack);
      if (put <= 0) return 0;
      slot.id = itemId;
      slot.count = put;
      return put;
    }

    if (slot.id !== itemId) return 0;
    const space = maxStack - slot.count;
    if (space <= 0) return 0;
    const put = Math.min(space, desiredCount);
    slot.count += put;
    return put;
  }

  function quickMoveIntoOpenFurnace(itemId, desiredCount) {
    if (!isOpen() || !itemDefs[itemId]) return 0;
    const state = currentState();
    if (!state) return 0;

    const total = Math.max(0, Math.floor(Number(desiredCount)));
    if (total <= 0) return 0;

    let remaining = total;
    if (isSmeltableItem(itemId)) {
      remaining -= quickMoveIntoSlot(state.input, itemId, remaining, isSmeltableItem);
    }
    if (remaining > 0 && isFuelItem(itemId)) {
      remaining -= quickMoveIntoSlot(state.fuel, itemId, remaining, isFuelItem);
    }

    const moved = total - remaining;
    if (moved > 0) renderOpenFurnace();
    return moved;
  }

  function handleSlotMouseDown(slotType, e) {
    const state = currentState();
    if (!state) return;
    if (e.button !== 0 && e.button !== 2) return;

    if (slotType === 'output') {
      ui.handleExternalOutputSlotMouseDown(state.output, e, {
        onTake({ itemId, count }) {
          if (typeof onTakeOutput === 'function' && itemId && count > 0) {
            onTakeOutput({ itemId, count });
          }
        },
        onChange: renderOpenFurnace
      });
      return;
    }

    const slot = slotType === 'input' ? state.input : state.fuel;
    const validator = slotType === 'input' ? isSmeltableItem : isFuelItem;
    ui.handleExternalSlotMouseDown(slot, e, {
      acceptsItem(itemId) {
        return validator(itemId);
      },
      onChange: renderOpenFurnace
    });
  }

  function ensureOpenFurnaceStillExists() {
    if (!openFurnaceKey) return;
    const pos = parseFurnaceKey(openFurnaceKey);
    if (world.getBlockIdAt(pos.x, pos.y, pos.z) === FURNACE_BLOCK_ID) return;
    furnaceStates.delete(openFurnaceKey);
    close();
  }

  function ejectContentsAt(x, y, z, onDrop = null) {
    const key = furnaceKeyFromPos(x, y, z);
    const state = furnaceStates.get(key);
    if (!state) return false;

    const pos = parseFurnaceKey(key);
    const emit = (stack) => {
      if (isStackEmpty(stack)) return;
      if (typeof onDrop === 'function') {
        onDrop({
          itemId: stack.id,
          count: stack.count,
          position: pos
        });
      }
    };

    emit(state.input);
    emit(state.fuel);
    emit(state.output);

    furnaceStates.delete(key);
    if (openFurnaceKey === key) close();
    return true;
  }

  function openAtBlock(block) {
    if (!block || block.userData?.id !== FURNACE_BLOCK_ID) return false;
    const key = furnaceKeyFromPos(block.position.x, block.position.y, block.position.z);
    openFurnaceKey = key;
    getOrCreateState(key);
    if (overlayEl) overlayEl.style.display = 'flex';
    document.body.classList.add('furnace-open');
    document.body.classList.add('inventory-open');
    renderOpenFurnace();
    return true;
  }

  function close() {
    openFurnaceKey = null;
    if (overlayEl) overlayEl.style.display = 'none';
    hideTooltip();
    document.body.classList.remove('furnace-open');
    if (!ui.isCraftOpen()) document.body.classList.remove('inventory-open');
  }

  function isOpen() {
    return !!openFurnaceKey;
  }

  function refreshLocale() {
    renderOpenFurnace();
  }

  function update(delta) {
    for (const state of furnaceStates.values()) {
      updateState(state, delta);
    }
    ensureOpenFurnaceStillExists();
    if (isOpen()) renderOpenFurnace();
  }

  function exportState() {
    const furnaces = [];
    for (const [key, state] of furnaceStates.entries()) {
      furnaces.push({
        key,
        input: cloneStackData(state.input),
        fuel: cloneStackData(state.fuel),
        output: cloneStackData(state.output),
        burnTime: Number(state.burnTime) || 0,
        burnTotal: Number(state.burnTotal) || 0,
        progress: Number(state.progress) || 0
      });
    }
    return { furnaces };
  }

  function importState(snapshot = null) {
    furnaceStates.clear();
    const list = Array.isArray(snapshot?.furnaces) ? snapshot.furnaces : [];
    for (const entry of list) {
      const key = String(entry?.key || '');
      if (!key) continue;

      const state = createFurnaceState();
      const input = normalizeStackData(entry?.input);
      const fuel = normalizeStackData(entry?.fuel);
      const output = normalizeStackData(entry?.output);
      if (input) {
        state.input.id = input.id;
        state.input.count = input.count;
      }
      if (fuel) {
        state.fuel.id = fuel.id;
        state.fuel.count = fuel.count;
      }
      if (output) {
        state.output.id = output.id;
        state.output.count = output.count;
      }
      state.burnTime = Math.max(0, Number(entry?.burnTime) || 0);
      state.burnTotal = Math.max(0, Number(entry?.burnTotal) || 0);
      state.progress = Math.max(0, Number(entry?.progress) || 0);
      furnaceStates.set(key, state);
    }

    ensureOpenFurnaceStillExists();
    if (isOpen()) renderOpenFurnace();
  }

  function quickMoveFromInventory({ itemId = null, count = 0 } = {}) {
    if (!itemId || count <= 0) return 0;
    return quickMoveIntoOpenFurnace(String(itemId), count);
  }

  function bindSlotInteraction(el, slotType) {
    if (!el) return;
    el.addEventListener('mousedown', (e) => {
      e.preventDefault();
      handleSlotMouseDown(slotType, e);
    });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  bindSlotInteraction(inputSlotEl, 'input');
  bindSlotInteraction(fuelSlotEl, 'fuel');
  bindSlotInteraction(outputSlotEl, 'output');
  bindSlotTooltip(inputSlotEl, () => currentState()?.input || null);
  bindSlotTooltip(fuelSlotEl, () => currentState()?.fuel || null);
  bindSlotTooltip(outputSlotEl, () => currentState()?.output || null);

  if (closeBtnEl) closeBtnEl.addEventListener('click', () => {
    if (typeof onRequestClose === 'function') onRequestClose();
    else close();
  });

  return {
    openAtBlock,
    close,
    isOpen,
    update,
    refreshLocale,
    quickMoveFromInventory,
    ejectContentsAt,
    exportState,
    importState
  };
}
